import { ElementRef, EventEmitter, OnChanges } from '@angular/core';
import { Logger } from 'app/shared/logger.service';
import { PrettyPrinter } from './pretty-printer.service';
import { CopierService } from 'app/shared/copier.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { tap } from 'rxjs/operators';
/**
 * If linenums is not set, this is the default maximum number of lines that
 * an example can display without line numbers.
 */
var DEFAULT_LINE_NUMS_COUNT = 10;
/**
 * Formatted Code Block
 *
 * Pretty renders a code block, used in the docs and API reference by the code-example and
 * code-tabs embedded components.
 * It includes a "copy" button that will send the content to the clipboard when clicked
 *
 * Example usage:
 *
 * ```
 * <aio-code
 *   [language]="ts"
 *   [linenums]="true"
 *   [path]="router/src/app/app.module.ts"
 *   [region]="animations-module">
 * </aio-code>
 * ```
 *
 *
 * Renders code provided through the `updateCode` method.
 */
var CodeComponent = /** @class */ (function () {
    function CodeComponent(snackbar, pretty, copier, logger) {
        this.snackbar = snackbar;
        this.pretty = pretty;
        this.copier = copier;
        this.logger = logger;
        this.ariaLabel = '';
        this.codeFormatted = new EventEmitter();
    }
    Object.defineProperty(CodeComponent.prototype, "code", {
        get: function () { return this._code; },
        /** Code that should be formatted with current inputs and displayed in the view. */
        set: function (code) {
            this._code = code;
            if (!this._code || !this._code.trim()) {
                this.showMissingCodeMessage();
            }
            else {
                this.formatDisplayedCode();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CodeComponent.prototype, "header", {
        get: function () { return this._header; },
        /** Optional header to be displayed above the code. */
        set: function (header) {
            this._header = header;
            this.ariaLabel = this.header ? "Copy code snippet from " + this.header : '';
        },
        enumerable: true,
        configurable: true
    });
    CodeComponent.prototype.ngOnChanges = function () {
        // If some inputs have changed and there is code displayed, update the view with the latest
        // formatted code.
        if (this.code) {
            this.formatDisplayedCode();
        }
    };
    CodeComponent.prototype.formatDisplayedCode = function () {
        var _this = this;
        var leftAlignedCode = leftAlign(this.code);
        this.setCodeHtml(leftAlignedCode); // start with unformatted code
        this.codeText = this.getCodeText(); // store the unformatted code as text (for copying)
        this.pretty
            .formatCode(leftAlignedCode, this.language, this.getLinenums(leftAlignedCode))
            .pipe(tap(function () { return _this.codeFormatted.emit(); }))
            .subscribe(function (c) { return _this.setCodeHtml(c); }, function (err) { });
    };
    /** Sets the message showing that the code could not be found. */
    CodeComponent.prototype.showMissingCodeMessage = function () {
        var src = this.path ? this.path + (this.region ? '#' + this.region : '') : '';
        var srcMsg = src ? " for\n" + src : '.';
        this.setCodeHtml("<p class=\"code-missing\">The code sample is missing" + srcMsg + "</p>");
    };
    /** Sets the innerHTML of the code container to the provided code string. */
    CodeComponent.prototype.setCodeHtml = function (formattedCode) {
        // **Security:** Code example content is provided by docs authors and as such its considered to
        // be safe for innerHTML purposes.
        this.codeContainer.nativeElement.innerHTML = formattedCode;
    };
    /** Gets the textContent of the displayed code element. */
    CodeComponent.prototype.getCodeText = function () {
        // `prettify` may remove newlines, e.g. when `linenums` are on. Retrieve the content of the
        // container as text, before prettifying it.
        // We take the textContent because we don't want it to be HTML encoded.
        return this.codeContainer.nativeElement.textContent;
    };
    /** Copies the code snippet to the user's clipboard. */
    CodeComponent.prototype.doCopy = function () {
        var code = this.codeText;
        var successfullyCopied = this.copier.copyText(code);
        if (successfullyCopied) {
            this.logger.log('Copied code to clipboard:', code);
            this.snackbar.open('Code Copied', '', { duration: 800 });
        }
        else {
            this.logger.error(new Error("ERROR copying code to clipboard: \"" + code + "\""));
            this.snackbar.open('Copy failed. Please try again!', '', { duration: 800 });
        }
    };
    /** Gets the calculated value of linenums (boolean/number). */
    CodeComponent.prototype.getLinenums = function (code) {
        var linenums = typeof this.linenums === 'boolean' ? this.linenums :
            this.linenums === 'true' ? true :
                this.linenums === 'false' ? false :
                    typeof this.linenums === 'string' ? parseInt(this.linenums, 10) :
                        this.linenums;
        // if no linenums, enable line numbers if more than one line
        return linenums == null || isNaN(linenums) ?
            (code.match(/\n/g) || []).length > DEFAULT_LINE_NUMS_COUNT : linenums;
    };
    return CodeComponent;
}());
export { CodeComponent };
function leftAlign(text) {
    var indent = Number.MAX_VALUE;
    var lines = text.split('\n');
    lines.forEach(function (line) {
        var lineIndent = line.search(/\S/);
        if (lineIndent !== -1) {
            indent = Math.min(lineIndent, indent);
        }
    });
    return lines.map(function (line) { return line.substr(indent); }).join('\n').trim();
}
